import { useState } from 'react';
import '../../styles/onboardingFlow/forgotPassword/forgotPassword.scss';
import { useNavigate } from 'react-router-dom';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { asyncHandler } from '../../lib/api/async-handler';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import { submitLogToBugsnag } from '../../lib/api/log';
import { OTPCodeInput } from '../../ui/inputs/OTPCodeInput';
import {
  FORGOT_PASSWORD_ENDPOINT,
  VERIFY_PASSWORD_OTP_ENDPOINT,
} from '../../lib/constants/central-api';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import { editSVG } from '../../lib/allCustomSVGs';
import adupLogoSet from '../../assets/onboarding/common/adup-logo-set.png';
import OnboardingMobileLogoPanel from '../onboardingComponents/OnboardingMobileLogoPanel';
import OnboardingBackButton from '../onboardingComponents/OnboardingBackButton';

const VerifyOTP = ({
  setIsEmailSent,
}: {
  setIsEmailSent: (isEmailSent: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [otpCode, setOTPCode] = useState('');
  const [otpKey, setOtpKey] = useState(0);
  const [error, setError] = useState('');
  const userEmail = useSelector((state: RootState) => state.user.profile.email);
  const isDisabled =
    !otpCode || otpCode?.length !== 6 || !userEmail || userEmail === ''
      ? true
      : false;

  async function onSubmitHandler(e: any) {
    e.preventDefault();
    setError('');

    try {
      const requestInfo: RequestInfo = {
        url: VERIFY_PASSWORD_OTP_ENDPOINT,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder({
          email: userEmail,
          otp: otpCode,
        }),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
      };

      const actionType = 'VERIFY_PASSWORD_OTP';
      const customMsg = t('VerifyOTPSuccess-msg');

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);

      const response = res.data;

      if (res.actionType === actionType && response.success) {
        const token = response?.data?.token;
        if (token) {
          navigate('/reset-password', { state: { token } });
        }
      } else {
        setError(response?.message || t('InvalidOTP'));
      }
    } catch (exception) {
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
      setError(t('InvalidOTP'));
    }
  }

  async function onResendOTP() {
    // Reset the OTP code to its initial state
    setOTPCode('');
    setError('');

    // Change the key to force re-render
    setOtpKey((prevKey) => prevKey + 1);
    try {
      const requestInfo: RequestInfo = {
        url: FORGOT_PASSWORD_ENDPOINT,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder({
          email: userEmail,
        }),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
      };

      const actionType = 'FORGOT_PASSWORD';
      const customMsg = t('ForgotPasswordSuccess-msg');

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);

      const response = res.data;

      if (res.actionType === actionType && response.success) {
        // OTP sent successfully
      }
    } catch (exception) {
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  return (
    <div className='onboarding-verify-otp'>
      {/* Logo panel (mobile only) */}
      <OnboardingMobileLogoPanel />

      <div className='onboarding-verify-otp__container'>
        {/* Back button (mobile only) */}
        <OnboardingBackButton
          onClick={() => setIsEmailSent(false)}
          aria-label={t('GoBack')}
        />

        {/* Logo (desktop only; hidden on mobile when logo panel is shown) */}
        <div className='onboarding-verify-otp__logo'>
          <img src={adupLogoSet} alt='AdUp Logo' />
        </div>

        {/* Heading */}
        <div className='onboarding-verify-otp__header'>
          <h1>{t('VerifyYourIdentity')}</h1>
          <div className='onboarding-verify-otp__email-display'>
            <span className='onboarding-verify-otp__email-text'>
              {t('EnterThe6DigitCodeSentToYourEmail')},{' '}
              <span className='onboarding-verify-otp__email-link'>
                {userEmail}
              </span>
            </span>
            <span
              className='onboarding-verify-otp__email-edit'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsEmailSent(false);
              }}
            >
              <CustomSVGs svg={editSVG} />
            </span>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmitHandler}
          className='onboarding-verify-otp__form'
        >
          {/* OTP Input */}
          <div
            className={`onboarding-verify-otp__otp-container ${
              error ? 'onboarding-verify-otp__otp-container--error' : ''
            }`}
          >
            <OTPCodeInput
              key={otpKey}
              numberOfFields={6}
              onComplete={(code: string) => {
                setOTPCode(code);
                setError('');
              }}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className='onboarding-verify-otp__error'>
              <span>{error}</span>
            </div>
          )}

          {/* Verify OTP Button */}
          <div className='onboarding-verify-otp__button'>
            <MainSquareButton
              value={t('VerifyOTP')}
              type='submit'
              disabled={isDisabled}
              classes='onboarding-verify-otp__continue-btn'
            />
          </div>

          {/* Resend OTP Link */}
          <div className='onboarding-verify-otp__resend'>
            <span>
              {t('DidNotReceiveCode')}{' '}
              <span
                className='onboarding-verify-otp__resend-link'
                onClick={onResendOTP}
              >
                {t('ResendOTP')}
              </span>
            </span>
          </div>

          {/* Sign In Link */}
          <div className='onboarding-verify-otp__signin'>
            <span>
              {t('RememberYourPassword')}{' '}
              <span
                className='onboarding-verify-otp__signin-link'
                onClick={() => navigate('/login')}
              >
                {t('SignIn')}
              </span>
            </span>
          </div>
        </form>
      </div>

      {/* Copyright */}
      <div className='onboarding-verify-otp__copyright'>
        <span>© AdUp {new Date().getFullYear()}</span>
      </div>
    </div>
  );
};

export default VerifyOTP;
